/**
 * YEET CANNON — Depth-Charge Mortar (doc 05). 2x2, 60 alloy, 200 HP, 30 dmg in
 * r2, 0.33/s, range 4–10 (min 4), 3 ammo/shot.
 *
 * "Throws problems at problems."
 *
 * Silhouette family: SQUAT-MORTAR. A wide low platform, a turntable, and one
 * fat stubby tube that leans toward its facing but always points mostly UP —
 * the lob is the identity. Widest and lowest thing in the tower class; the
 * black-shape test cannot confuse it with a mast or a coil.
 *
 * Signature: warm red loader lamp on the turntable (doc 10 families).
 */

import { facingOffset, tiersOf, type BuildContext, type EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_TOWER } from '../materials.ts';
import { isoCylinder, isoDiamondRim, isoPrism, linePx, type EntityCanvas } from '../../render/primitives.ts';

/**
 * Upgrade motifs (doc 05's 3×3):
 *   A · BIGGER BOOM     the tube fattens and lengthens, ending in a blast plate
 *   B · CLUSTER MUCK    bomblet racks, ending in a rotary cluster drum
 *   C · RUDE SHOCKWAVE  pressure vanes, ending in a shock ring on the platform
 */
export const drawYeet: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_TOWER);
  const lamp = signatureColor(sig, ctx.state);
  const up = tiersOf(ctx);

  // platform across the full 2x2, with a service rim
  isoPrism(c, { n: 2, inset: 4, height: 4, ramp: r });
  isoDiamondRim(c.s, c.ax, c.ay - 4, 12, r.edge);

  // turntable
  const tableTop = isoCylinder(c, { rx: 11, height: 4, lift: 4, ramp: r });

  // THE TUBE — it is the whole identity, so it is fat, tall and dark against
  // the lit platform. Tracking/firing raise it 2 px ("spooling the lob");
  // firing kicks it back down along the recoil.
  const lean = facingOffset(ctx.facing, 9);
  const kick = ctx.state === 'firing' ? 1 - Math.round(ctx.phase) : 0;
  const armed = ctx.state === 'tracking' || ctx.state === 'firing' ? 2 : 0;
  const baseX = c.ax - Math.round(lean.x / 5);
  const baseY = tableTop + 2;
  // path A buys calibre: a longer, fatter tube reads before any number does
  const bore = 9 + (up.a >= 2 ? 2 : 0) + (up.a >= 3 ? 3 : 0);
  const barrel = 13 + (up.a >= 1 ? 1 : 0) + (up.a >= 3 ? 1 : 0);
  const tipX = baseX + Math.round(lean.x / 2);
  const tipY = baseY - barrel - armed + Math.round(lean.y / 2) + kick;
  // breech collar where the tube meets the turntable
  isoCylinder(c, { rx: 6, height: 3, lift: c.ay - baseY, dx: baseX - c.ax, ramp: r });
  linePx(c.s, baseX, baseY - 2, tipX, tipY, r.deep, bore);
  linePx(c.s, baseX - 2, baseY - 2, tipX - 2, tipY, r.left, 4);
  linePx(c.s, baseX - 4, baseY - 2, tipX - 4, tipY + 1, r.edge, 1); // lit rim line
  // muzzle: a wide dark bore ringed by the lit lip
  const lip = 4 + (up.a >= 2 ? 1 : 0);
  c.s.fillRect(tipX - lip, tipY - 2, lip * 2 + 1, 4, r.edge);
  c.s.fillRect(tipX - lip + 1, tipY - 1, lip * 2 - 1, 2, r.deep);
  if (up.a >= 1) c.s.fillRect(tipX - lip - 1, tipY - 3, lip * 2 + 3, 1, r.edge); // muzzle brake
  if (up.a >= 3) {
    // "Michael Bay Mode": a blast deflector plate behind the muzzle
    const mx = baseX + Math.round(lean.x / 4);
    const my = baseY - Math.round(barrel * 0.55);
    c.s.fillRect(mx - 7, my - 1, 15, 2, r.left);
    c.s.fillRect(mx - 7, my - 2, 15, 1, r.edge);
  }

  // ammo drum beside the tube — path B swaps it for a twin cluster magazine
  const drumH = up.b >= 3 ? 9 : 6;
  isoCylinder(c, { rx: 5, height: drumH, lift: 8, dx: 12, dy: 5, ramp: r });
  c.s.fillRect(c.ax + 9, c.ay - 6 - drumH, 6, 1, r.edge);
  if (up.b >= 3) {
    // "Confetti of Pain": a second drum and a feed chute — the tower gets a
    // whole extra bulge on its right side, which is the silhouette delta
    isoCylinder(c, { rx: 5, height: 11, lift: 5, dx: 20, dy: 7, ramp: r });
    linePx(c.s, c.ax + 10, c.ay - 12, c.ax + 2, c.ay - 6, r.left, 3);
    c.s.fillRect(c.ax + 15, c.ay - 17, 10, 1, r.edge);
    c.s.fillRect(c.ax + 18, c.ay - 21, 5, 4, r.left);
  }

  // --- B · CLUSTER MUCK: racks of little ones -------------------------------
  if (up.b >= 1) {
    for (let k = 0; k < 3; k++) c.s.fillRect(c.ax - 13 + k * 4, c.ay - 7, 3, 3, r.left);
    c.s.fillRect(c.ax - 14, c.ay - 4, 12, 1, r.edge);
  }
  if (up.b >= 2) {
    for (let k = 0; k < 3; k++) c.s.fillRect(c.ax - 13 + k * 4, c.ay - 11, 3, 3, r.left);
    c.s.fillRect(c.ax - 14, c.ay - 8, 12, 1, r.edge);
  }
  if (up.b >= 3) {
    // "Confetti of Pain": the drum is a revolver, and it shows its chambers
    for (let k = 0; k < 4; k++) {
      c.s.px(c.ax + 10 + (k % 2) * 4, c.ay - 8 - Math.floor(k / 2) * 4, r.deep);
    }
    c.s.fillRect(c.ax + 8, c.ay - 5 - drumH, 10, 1, sig.on);
  }

  // --- C · RUDE SHOCKWAVE: pressure vanes, then the ring --------------------
  if (up.c >= 1) {
    for (const side of [-1, 1]) c.s.fillRect(c.ax + side * 9 - 1, c.ay - 9, 2, 5, r.left);
  }
  if (up.c >= 2) {
    for (const side of [-1, 1]) c.s.fillRect(c.ax + side * 5 - 1, c.ay - 13, 2, 4, r.left);
    isoCylinder(c, { rx: 3, height: 4, lift: 4, dx: -11, dy: 4, ramp: r });
  }
  if (up.c >= 3) {
    // "Undertow Uppercut": a shock ring rides the platform edge
    isoDiamondRim(c.s, c.ax, c.ay - 5, 13, sig.on);
    isoDiamondRim(c.s, c.ax, c.ay - 7, 11, r.edge);
  }

  if (lamp !== null) {
    c.s.fillRect(c.ax - 8, c.ay - 8, 3, 2, lamp);
  }
  if (ctx.state === 'firing' && ctx.phase < 0.4) {
    // muzzle flash, two frames, snapped to the grid
    c.s.fillRect(tipX - 1, tipY - 3, 3, 2, sig.hot, 'set');
    c.s.px(tipX - 3, tipY - 2, sig.halo);
    c.s.px(tipX + 3, tipY - 2, sig.halo);
  }
  if (ctx.state === 'tracking') {
    // a shell sits proud at the muzzle, ready to be yeeted
    c.s.fillRect(tipX - 1, tipY - 1, 3, 2, r.edge, 'set');
  }
  if (ctx.state === 'dry') {
    // empty drum: open hatch, plugged bore, dark band on the platform
    c.s.fillRect(c.ax + 7, c.ay - 11, 6, 2, r.deep, 'set');
    c.s.fillRect(tipX - 2, tipY - 1, 5, 3, r.deep, 'set');
    c.s.fillRect(c.ax - 7, c.ay - 3, 14, 1, r.deep);
  }
};
